import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/dealership/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Panel, StatusPill, PillTabs } from "@/components/lux";
import { computeWorksheet, deepPatch, money, STATUS_TONE } from "@/dealership/lib/desk";
import VehiclePanel from "@/dealership/components/desk/VehiclePanel";
import TradePanel from "@/dealership/components/desk/TradePanel";
import FeesPanel from "@/dealership/components/desk/FeesPanel";
import ProductsPanel from "@/dealership/components/desk/ProductsPanel";
import FinancePanel from "@/dealership/components/desk/FinancePanel";
import SummaryPanel from "@/dealership/components/desk/SummaryPanel";
import PaymentGrid from "@/dealership/components/desk/PaymentGrid";
import MenuSelling from "@/dealership/components/desk/MenuSelling";
import SolveDialog from "@/dealership/components/desk/SolveDialog";
import { ArrowLeft, Calculator, Copy, FileText, Printer, Trash2, CheckCircle2, Users, GitCompare } from "lucide-react";

const STATUS_FLOW = ["worksheet", "presented", "accepted"];

export default function DeskWorksheet() {
  const { wsId } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();
  const internal = user?.role === "admin" || user?.role === "staff";

  const [ws, setWs] = useState(null);
  const [saving, setSaving] = useState(false);
  const [solveOpen, setSolveOpen] = useState(false);
  const [compareOpen, setCompareOpen] = useState(false);
  const dirty = useRef(false);
  const timer = useRef(null);

  const { data: settings } = useQuery({ queryKey: ["desk-settings"], queryFn: async () => (await api.get("/desking/settings")).data });
  const { data: loaded } = useQuery({ queryKey: ["worksheet", wsId], queryFn: async () => (await api.get(`/desking/worksheets/${wsId}`)).data });
  const { data: siblings = [] } = useQuery({
    queryKey: ["worksheet-siblings", loaded?.lead_id, loaded?.vehicle_id],
    enabled: Boolean(loaded && (loaded.lead_id || loaded.vehicle_id)),
    queryFn: async () => {
      const params = loaded.lead_id ? { lead_id: loaded.lead_id } : { vehicle_id: loaded.vehicle_id };
      return (await api.get("/desking/worksheets", { params })).data;
    },
  });

  useEffect(() => { if (loaded && !ws) setWs(loaded); }, [loaded, ws]);
  useEffect(() => { setWs(null); dirty.current = false; }, [wsId]);

  const totals = useMemo(() => (ws && settings ? computeWorksheet(ws, settings) : null), [ws, settings]);

  // Autosave — no calculate button, no save button needed.
  useEffect(() => {
    if (!ws || !dirty.current) return;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setSaving(true);
      try {
        const { totals: _t, id, created_at, updated_at, created_by, converted_deal_id, ...payload } = ws;
        await api.put(`/desking/worksheets/${wsId}`, payload);
        qc.invalidateQueries({ queryKey: ["worksheet-siblings"] });
        dirty.current = false;
      } catch { toast.error("Could not save the worksheet"); }
      finally { setSaving(false); }
    }, 700);
    return () => timer.current && clearTimeout(timer.current);
  }, [ws, wsId, qc]);

  const set = (patch) => { dirty.current = true; setWs((cur) => deepPatch(cur, patch)); };

  const setStatus = async (status) => {
    const { data } = await api.post(`/desking/worksheets/${wsId}/status`, { status });
    setWs((cur) => ({ ...cur, status: data.status }));
    qc.invalidateQueries({ queryKey: ["worksheet-siblings"] });
    qc.invalidateQueries({ queryKey: ["worksheets"] });
    toast.success(`Marked ${status}`);
  };

  const duplicate = async () => {
    const { data } = await api.post(`/desking/worksheets/${wsId}/duplicate`);
    qc.invalidateQueries({ queryKey: ["worksheet-siblings"] });
    navigate(`/dealership/desking/${data.id}`);
    toast.success("Scenario duplicated");
  };

  const convert = async () => {
    const label = `${money(totals.out_the_door)} out-the-door`;
    if (!window.confirm(`Convert "${ws.name}" into a real deal and bill of sale (${label})? This marks the vehicle sold.`)) return;
    try {
      const { data } = await api.post(`/desking/worksheets/${wsId}/convert`);
      qc.invalidateQueries({ queryKey: ["deals"] });
      toast.success(`Deal ${data.deal.bill_number || ""} created`);
      navigate(`/dealership/deals/${data.deal.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not convert this worksheet");
    }
  };

  const remove = async () => {
    if (!window.confirm("Delete this worksheet?")) return;
    await api.delete(`/desking/worksheets/${wsId}`);
    navigate("/dealership/desking");
  };

  if (!ws || !settings || !totals) return <div className="p-8 text-muted-foreground">Loading worksheet…</div>;

  const scenarioTabs = siblings.map((s) => ({ key: s.id, label: s.name || "Worksheet", testid: `scenario-${s.id}` }));

  return (
    <div className="p-6 sm:p-8 pb-16" data-testid="desk-worksheet-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div className="min-w-0">
          <button onClick={() => navigate("/dealership/desking")} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1.5 mb-2" data-testid="desk-back">
            <ArrowLeft className="h-3.5 w-3.5" /> All worksheets
          </button>
          <div className="flex items-center gap-3 flex-wrap">
            <Input
              value={ws.name} onChange={(e) => set({ name: e.target.value })}
              className="h-11 font-heading text-2xl md:text-2xl border-transparent bg-transparent px-0 focus-visible:border-border w-auto min-w-[16rem]"
              data-testid="desk-name"
            />
            <StatusPill status={ws.status} tone={STATUS_TONE[ws.status]} testid="desk-status" />
            <span className="text-xs text-muted-foreground">{saving ? "Saving…" : dirty.current ? "Unsaved" : "Saved"}</span>
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            {ws.customer_name || "No customer"} · {ws.vehicle_desc || "no vehicle"}
            {ws.converted_deal_id && <> · <Link to={`/dealership/deals/${ws.converted_deal_id}`} className="text-primary hover:underline">view deal</Link></>}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button size="sm" variant="outline" onClick={() => setSolveOpen(true)} data-testid="desk-solve-btn"><Calculator className="h-3.5 w-3.5 mr-1.5" />Solve</Button>
          {siblings.length > 1 && <Button size="sm" variant="outline" onClick={() => setCompareOpen(true)} data-testid="desk-compare-btn"><GitCompare className="h-3.5 w-3.5 mr-1.5" />Compare</Button>}
          <Button size="sm" variant="outline" onClick={duplicate} data-testid="desk-duplicate"><Copy className="h-3.5 w-3.5 mr-1.5" />Scenario</Button>
          <Button size="sm" variant="outline" onClick={() => window.open(`/dealership/desking/${wsId}/quote`, "_blank")} data-testid="desk-quote-btn"><FileText className="h-3.5 w-3.5 mr-1.5" />Customer quote</Button>
          {internal && <Button size="sm" variant="ghost" onClick={() => window.open(`/dealership/desking/${wsId}/quote?internal=1`, "_blank")} data-testid="desk-print-internal"><Printer className="h-3.5 w-3.5 mr-1.5" />Internal</Button>}
          <Button size="sm" variant="ghost" className="text-destructive" onClick={remove} data-testid="desk-delete"><Trash2 className="h-3.5 w-3.5" /></Button>
        </div>
      </div>

      {/* Scenario switcher + deal state */}
      <div className="flex items-center justify-between gap-4 flex-wrap mb-6">
        {scenarioTabs.length > 1 && (
          <PillTabs tabs={scenarioTabs} value={wsId} onChange={(id) => navigate(`/dealership/desking/${id}`)} size="sm" testid="desk-scenarios" />
        )}
        <div className="flex items-center gap-2 ml-auto">
          {STATUS_FLOW.map((s) => (
            <Button key={s} size="sm" variant={ws.status === s ? "default" : "outline"} onClick={() => setStatus(s)} data-testid={`desk-set-${s}`} className="capitalize">
              {s}
            </Button>
          ))}
          <Button size="sm" onClick={convert} disabled={ws.status === "converted"} data-testid="desk-convert">
            <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />{ws.status === "converted" ? "Converted" : "Convert to deal"}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="xl:col-span-2 space-y-4">
          <Panel label="Customer" title={ws.customer_name || "Customer"} icon={Users} testid="desk-customer-panel">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Input value={ws.customer_name || ""} onChange={(e) => set({ customer_name: e.target.value })} placeholder="Customer name" className="h-9" data-testid="desk-customer-name" />
              <Input value={ws.customer_phone || ""} onChange={(e) => set({ customer_phone: e.target.value })} placeholder="Phone" className="h-9" data-testid="desk-customer-phone" />
              <Input value={ws.customer_email || ""} onChange={(e) => set({ customer_email: e.target.value })} placeholder="Email" className="h-9" data-testid="desk-customer-email" />
              <Input value={ws.salesperson || ""} onChange={(e) => set({ salesperson: e.target.value })} placeholder="Salesperson" className="h-9" data-testid="desk-salesperson" />
              <Input value={ws.customer_address || ""} onChange={(e) => set({ customer_address: e.target.value })} placeholder="Address" className="h-9 sm:col-span-2" />
            </div>
          </Panel>

          <VehiclePanel ws={ws} set={set} totals={totals} internal={internal} />
          <TradePanel ws={ws} set={set} totals={totals} internal={internal} />
          <FeesPanel ws={ws} set={set} totals={totals} settings={settings} />
          <ProductsPanel ws={ws} set={set} totals={totals} settings={settings} internal={internal} />
          <FinancePanel ws={ws} set={set} settings={settings} />
          {totals.mode !== "cash" && <PaymentGrid ws={ws} settings={settings} onApply={set} />}
          {totals.mode !== "cash" && <MenuSelling ws={ws} settings={settings} onApply={set} />}

          <Panel label="Notes" title="Desk notes" testid="desk-notes-panel">
            <Textarea rows={3} value={ws.notes || ""} onChange={(e) => set({ notes: e.target.value })} placeholder="Anything the F&I office should know…" data-testid="desk-notes" />
            <div className="grid grid-cols-2 gap-3 mt-3">
              <div>
                <div className="label-mono mb-1.5">Quote valid until</div>
                <Input type="date" value={ws.valid_until || ""} onChange={(e) => set({ valid_until: e.target.value })} className="h-9" data-testid="desk-valid-until" />
              </div>
            </div>
          </Panel>
        </div>

        <div className="xl:sticky xl:top-24 h-fit">
          <SummaryPanel ws={ws} totals={totals} internal={internal} />
        </div>
      </div>

      <SolveDialog open={solveOpen} onOpenChange={setSolveOpen} ws={ws} settings={settings} onApply={set} />

      <Dialog open={compareOpen} onOpenChange={setCompareOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="desk-compare-dialog">
          <DialogHeader>
            <DialogTitle>Compare scenarios</DialogTitle>
            <DialogDescription>Every structure on this customer or vehicle, side by side.</DialogDescription>
          </DialogHeader>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="px-3 py-3 label-mono font-normal text-left">Scenario</th>
                  {["Price", "Trade", "Products", "Amount financed", "Term", "Rate", "Payment", ...(internal ? ["Gross"] : [])].map((h) => (
                    <th key={h} className="px-3 py-3 label-mono font-normal text-right">{h}</th>
                  ))}
                  <th />
                </tr>
              </thead>
              <tbody>
                {siblings.map((s) => {
                  const t = s.totals;
                  return (
                    <tr key={s.id} className={`border-b border-border/50 ${s.id === wsId ? "bg-primary/[0.06]" : ""}`} data-testid={`compare-row-${s.id}`}>
                      <td className="px-3 py-3">
                        <div className="font-heading">{s.name}</div>
                        <StatusPill status={s.status} tone={STATUS_TONE[s.status]} />
                      </td>
                      <td className="px-3 py-3 text-right tnum">{money(t.selling_price_net)}</td>
                      <td className="px-3 py-3 text-right tnum">{money(t.trade_allowance)}</td>
                      <td className="px-3 py-3 text-right tnum">{money(t.products_total)}</td>
                      <td className="px-3 py-3 text-right tnum">{t.mode === "cash" ? "Cash" : money(t.amount_financed)}</td>
                      <td className="px-3 py-3 text-right tnum">{t.mode === "cash" ? "—" : `${t.term_months}mo`}</td>
                      <td className="px-3 py-3 text-right tnum">{t.mode === "cash" ? "—" : `${t.apr}%`}</td>
                      <td className="px-3 py-3 text-right"><span className="money font-heading">{t.mode === "cash" ? money(t.cash_due) : money(t.payment)}</span></td>
                      {internal && <td className="px-3 py-3 text-right money tnum">{money(t.total_gross)}</td>}
                      <td className="px-3 py-3 text-right">
                        {s.id !== wsId && <Button size="sm" variant="outline" onClick={() => { setCompareOpen(false); navigate(`/dealership/desking/${s.id}`); }}>Open</Button>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
